import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createTeacher, listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { getUserInfo, getSucursalInfo } from "../services/auth";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 3;
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const initialState = {
  name: "",
  email: "",
  contact: "",
  birthDay: "",
  birthMonth: "",
  birthYear: "",
  maritalStatus: "Solteiro",
  educationalLevel: "Ensino Médio",
  selectedSubjects: [],
};

const CadastrarProfessorLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [subjects, setSubjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(true);

  useEffect(() => {
    listFrequencies()
      .then((resp) => {
        const frequencies = resp.data || [];
        return Promise.all(frequencies.map((f) => listSubjectsByLevel(f.id)));
      })
      .then((results) => {
        const merged = results.flatMap((resp) => resp.data || []);
        setSubjects(merged);
      })
      .catch(() => setSubjects([]))
      .finally(() => setSubjectsLoading(false));
  }, []);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const birthDate = useMemo(() => {
    if (!form.birthDay || !form.birthMonth || !form.birthYear) return "";
    return `${form.birthYear}-${String(form.birthMonth).padStart(2, "0")}-${String(
      form.birthDay,
    ).padStart(2, "0")}`;
  }, [form.birthDay, form.birthMonth, form.birthYear]);

  const stepLabels = [
    t("professor.wizard.step.info"),
    t("professor.wizard.step.confirm"),
    t("professor.wizard.step.success"),
  ];

  // Matches the original teacher/create form: only name and contact gate
  // advancing past step 1 (birth date/marital status/education level all
  // have defaults, email is optional but format-checked when present).
  const canAdvanceFromStep1 = form.name && form.contact;

  const toggleSubject = (id) => {
    setForm((f) => ({
      ...f,
      selectedSubjects: f.selectedSubjects.includes(id)
        ? f.selectedSubjects.filter((s) => s !== id)
        : [...f.selectedSubjects, id],
    }));
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!canAdvanceFromStep1) {
        toast.error(t("validation.required"));
        return;
      }
      if (form.contact.length !== 9) {
        toast.error(t("validation.required"));
        return;
      }
      if (form.email && !EMAIL_PATTERN.test(form.email)) {
        toast.error(t("validation.email.invalid"));
        return;
      }
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const submit = async () => {
    setSubmitting(true);
    const userId = getUserInfo()?.id;
    const subjectPayload = subjects
      .filter((s) => form.selectedSubjects.includes(s.id))
      .map((s) => ({ id: s.id, name: s.subjectName }));

    // The original teacher/create form has no UI controls for gender,
    // idNumber, taxId, contractType, isPermanent, lastEducationYear,
    // country or branchId — it always submits these exact hardcoded
    // values regardless of who's being registered. Kept as-is for
    // fidelity; there's nothing to preserve from "user intent" since no
    // control for them exists in the original.
    const payload = {
      name: form.name,
      email: form.email,
      contact: form.contact,
      address: "",
      picture: "",
      maritalStatus: form.maritalStatus,
      educationLevel: form.educationalLevel,
      activatedBy: userId,
      createdBy: userId,
      sucursalId: getSucursalInfo()?.id,
      birthDate,
      subject: subjectPayload,
      gender: "Male",
      idNumber: "0",
      taxId: "00",
      contractType: "TP",
      isPermanent: true,
      lastEducationYear: "2025",
      country: "MZ",
      branchId: "IT",
    };

    try {
      await createTeacher(payload);
      setDone(true);
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("professor.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("professor.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("professor.field.name")} <span className='required-asterisk'>*</span></label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="f7:person" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={t("professor.field.name.placeholder")}
                          value={form.name}
                          onChange={setField("name")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("professor.field.email")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mage:email" />
                        </span>
                        <input
                          type='email'
                          className='form-control'
                          placeholder={t("professor.field.email.placeholder")}
                          value={form.email}
                          onChange={setField("email")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("professor.field.contact")} <span className='required-asterisk'>*</span></label>
                      <div className='input-group'>
                        <span className='input-group-text'>+258</span>
                        <input
                          type='text'
                          maxLength={9}
                          className='form-control'
                          value={form.contact}
                          onChange={setField("contact")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-8'>
                      <label className='form-label'>{t("professor.field.birthDate")}</label>
                      <div className='row gx-2'>
                        <div className='col-4'>
                          <select
                            className='form-control form-select'
                            value={form.birthDay}
                            onChange={setField("birthDay")}
                          >
                            <option value=''>{t("professor.field.birthDay")}</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='col-4'>
                          <select
                            className='form-control form-select'
                            value={form.birthMonth}
                            onChange={setField("birthMonth")}
                          >
                            <option value=''>{t("professor.field.birthMonth")}</option>
                            {MONTH_NAMES.map((name, idx) => (
                              <option key={name} value={idx + 1}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='col-4'>
                          <select
                            className='form-control form-select'
                            value={form.birthYear}
                            onChange={setField("birthYear")}
                          >
                            <option value=''>{t("professor.field.birthYear")}</option>
                            {Array.from(
                              { length: 80 },
                              (_, i) => new Date().getFullYear() - 18 - i,
                            ).map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("professor.field.maritalStatus")}</label>
                      <select
                        className='form-control form-select'
                        value={form.maritalStatus}
                        onChange={setField("maritalStatus")}
                      >
                        <option value='Solteiro'>{t("global.single")}</option>
                        <option value='Casado'>{t("global.married")}</option>
                        <option value='Divorciado'>{t("global.divorced")}</option>
                        <option value='Viuvo'>{t("global.widower")}</option>
                      </select>
                    </div>
                    <div className='col-sm-4'>
                      <label className='form-label'>
                        {t("professor.field.educationalLevel")}
                      </label>
                      <select
                        className='form-control form-select'
                        value={form.educationalLevel}
                        onChange={setField("educationalLevel")}
                      >
                        <option value='Ensino Médio'>{t("global.middle.school")}</option>
                        <option value='Ensino Superior'>{t("global.bachelor")}</option>
                        <option value='Pós-graduação'>{t("global.post.graduated")}</option>
                        <option value='Mestrado'>{t("global.master")}</option>
                      </select>
                    </div>
                    <div className='col-sm-12'>
                      <label className='form-label'>{t("professor.field.subjects")}</label>
                      <div className='d-flex flex-wrap gap-8'>
                        {subjectsLoading && (
                          <span className='text-secondary-light text-sm'>
                            {t("professor.list.loading")}
                          </span>
                        )}
                        {!subjectsLoading && subjects.length === 0 && (
                          <span className='text-secondary-light text-sm'>
                            {t("professor.field.subjects.empty")}
                          </span>
                        )}
                        {subjects.map((subject) => {
                          const active = form.selectedSubjects.includes(subject.id);
                          return (
                            <button
                              type='button'
                              key={subject.id}
                              onClick={() => toggleSubject(subject.id)}
                              className={`btn btn-sm radius-8 ${
                                active ? "btn-primary-600" : "btn-outline-secondary"
                              }`}
                            >
                              {subject.subjectName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className='form-group text-end mt-24'>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("professor.confirm.description")}</div>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("global.personal.information")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("professor.field.name")}:</strong> {form.name}
                        </li>
                        <li>
                          <strong>{t("professor.field.email")}:</strong> {form.email}
                        </li>
                        <li>
                          <strong>{t("professor.field.contact")}:</strong> +258 {form.contact}
                        </li>
                        <li>
                          <strong>{t("professor.field.birthDate")}:</strong> {birthDate}
                        </li>
                      </ul>
                    </div>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("global.degree.info")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("professor.field.maritalStatus")}:</strong>{" "}
                          {form.maritalStatus}
                        </li>
                        <li>
                          <strong>{t("professor.field.educationalLevel")}:</strong>{" "}
                          {form.educationalLevel}
                        </li>
                        <li>
                          <strong>{t("professor.field.subjects")}:</strong>{" "}
                          {subjects
                            .filter((s) => form.selectedSubjects.includes(s.id))
                            .map((s) => s.subjectName)
                            .join(", ") || "-"}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={submit}
                      type='button'
                      className='form-wizard-submit btn btn-primary-600 px-32'
                      disabled={submitting}
                    >
                      {submitting ? (
                        "..."
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>
                      {t("professor.create.success.title")}
                    </h6>
                    <p className='text-neutral-400 text-sm mb-0'>
                      {t("professor.create.success.subtitle")}
                    </p>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button
                      type='button'
                      className='btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={restart}
                    >
                      {t("professor.create.new")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/professores/lista")}
                    >
                      <i className='ri-arrow-left-line' /> {t("professor.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarProfessorLayer;
